import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification, successNofication } from 'src/shared/components/Notification';

// ─── Toggle Switch ────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange, disabled = false }) => (
    <div
        onClick={() => !disabled && onChange(!checked)}
        style={{
            width: 28, height: 16, borderRadius: 8,
            background: checked ? '#16a34a' : '#cbd5e1',
            position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s', flexShrink: 0,
            opacity: disabled ? 0.5 : 1, display: 'inline-block',
        }}
    >
        <div style={{
            position: 'absolute', top: 2,
            left: checked ? 14 : 2,
            width: 12, height: 12, borderRadius: '50%',
            background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
            transition: 'left 0.2s',
        }} />
    </div>
);

// ─── Field definitions ────────────────────────────────────────────────────────
const PREFERENCE_FIELDS = [
    { key: 'orderEditableEnabled', label: 'Order Editable', icon: '✏️', description: 'Allow orders to be edited after creation' },
    { key: 'customerAddressEditEnabled', label: 'Address Edit', icon: '📍', description: 'Allow customer address changes' },
    { key: 'addNewOrderEnabled', label: 'Add Order', icon: '➕', description: 'Allow creation of new orders' },
    { key: 'dateConversionRequired', label: 'Date Conv.', icon: '📅', description: 'Convert dates to local timezone' },
    { key: 'customerMobileMaskEnabled', label: 'Mobile Mask', icon: '🔒', description: 'Mask customer mobile numbers' },
    { key: 'merchantEnabled', label: 'Merchant', icon: '🏪', description: 'Enable merchant functionality' },
    { key: 'securityFlowEnabled', label: 'Security Flow', icon: '🛡️', description: 'Enable security approval workflow' },
    { key: 'pickupEnabled', label: 'Pickup', icon: '📦', description: 'Enable Pickup order type' },
    { key: 'dropOffEnabled', label: 'Drop-off', icon: '🚚', description: 'Enable Drop-off order type' },
    { key: 'pickAndDropEnabled', label: 'Pick & Drop', icon: '🔄', description: 'Enable Pick & Drop order type' },
    { key: 'licenseCancelEnabled', label: 'Cancel Lic.', icon: '🪪', description: 'Enable license cancellation functionality' },
    { key: 'orderCancelEnabled', label: 'Cancel Order', icon: '❌', description: 'Enable order cancellation capability' },
    { key: 'zoneBasedDlvApplicable', label: 'Delivery Rate', icon: '💰', description: 'Enable delivery rate configuration' },
    { key: 'itemPriceApiEnabled', label: 'Item Price API', icon: '🏷️', description: 'Enable Item Price API integration' },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const OrderPreferenceSettings = () => {
    const [preferences, setPreferences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(null); // preferenceId being saved
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchPreferences();
    }, []);

    const fetchPreferences = async () => {
        setLoading(true);
        try {
            debugger;
            const res = await PndRestApi.GetOrderPreferences();
            if (res?.data?.status === 200 && Array.isArray(res.data.data)) {
                const initialized = res.data.data.map(p => ({
                    ...p,
                    pickupEnabled: !!p.pickupEnabled,
                    dropOffEnabled: !!p.dropOffEnabled,
                    pickAndDropEnabled: !!p.pickAndDropEnabled,
                    licenseCancelEnabled: !!p.licenseCancelEnabled,
                    orderCancelEnabled: !!p.orderCancelEnabled,
                    zoneBasedDlvApplicable: !!p.zoneBasedDlvApplicable,
                    itemPriceApiEnabled: !!p.itemPriceApiEnabled,
                }));
                setPreferences(initialized);
            } else {
                showNotification('theme-light', 'ltr', 'Error', 'Failed to load preferences.');
            }
        } catch (e) {
            showNotification('theme-light', 'ltr', 'Error', 'Network error loading preferences.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (preferenceId, field, value) => {
        setPreferences(prev =>
            prev.map(p => p.preferenceId === preferenceId ? { ...p, [field]: value } : p)
        );
    };

    const handleSave = async (preference) => {
        setSaving(preference.preferenceId);
        try {
            const res = await PndRestApi.UpdateOrderPreferences(preference);
            if (res?.data?.status === 200) {
                successNofication('theme-light', 'ltr', 'Saved', `Customer Preferences updated.`);
            } else {
                showNotification('theme-light', 'ltr', 'Error', res?.data?.message || 'Save failed.');
            }
        } catch (e) {
            showNotification('theme-light', 'ltr', 'Error', 'Network error while saving.');
        } finally {
            setSaving(null);
        }
    };

    const filtered = preferences.filter(p =>
        String(p.customerId).includes(search.trim()) ||
        (p.customerName || '').toLowerCase().includes(search.trim().toLowerCase())
    );

    return (
        <Card className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
            {/* ── Header ── */}
            <CardHeader className="border-0 py-2 px-3" style={{
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
                borderTopLeftRadius: 12, borderTopRightRadius: 12,
            }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <div style={{ background: 'rgba(255,255,255,0.15)', padding: '6px 10px', borderRadius: 8 }}>
                            <span style={{ fontSize: '1.2rem' }}>⚙️</span>
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-white" style={{ fontSize: '1.05rem' }}>Order Preference Settings</h5>
                            <p className="mb-0 text-white opacity-75" style={{ fontSize: '0.75rem' }}>
                                Configure per-customer order workflow settings
                            </p>
                        </div>
                    </div>

                    {/* Search */}
                    <div style={{ position: 'relative', minWidth: 180 }}>
                        <input
                            type="text"
                            placeholder="Search customer..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{
                                padding: '4px 10px 4px 28px', borderRadius: 16,
                                border: 'none', fontSize: '0.78rem',
                                background: 'rgba(255,255,255,0.15)', color: '#fff',
                                outline: 'none', width: '100%',
                            }}
                        />
                        <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', opacity: 0.7, fontSize: '0.75rem' }}>🔍</span>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="p-0">
                {loading && <LinearProgress />}

                {!loading && filtered.length === 0 && (
                    <div className="text-center py-5 text-muted">
                        <div style={{ fontSize: '2.5rem' }}>🔍</div>
                        <p>No preferences found.</p>
                    </div>
                )}

                {/* ── Settings Table ── */}
                {filtered.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                    <th style={thStyle(150, 'left')}>Customer</th>
                                    {PREFERENCE_FIELDS.map(f => (
                                        <th key={f.key} style={thStyle(62, 'center')}>
                                            <Tooltip title={f.description} arrow placement="top">
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, cursor: 'help' }}>
                                                    <span style={{ fontSize: '0.95rem' }}>{f.icon}</span>
                                                    <span style={{ fontSize: '0.64rem', fontWeight: 700, color: '#475569', textAlign: 'center', lineHeight: 1.1 }}>{f.label}</span>
                                                </div>
                                            </Tooltip>
                                        </th>
                                    ))}
                                    <th style={thStyle(70, 'center')}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((pref, idx) => (
                                    <tr
                                        key={pref.preferenceId}
                                        style={{
                                            background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                                            borderBottom: '1px solid #e2e8f0',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                                        onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f8fafc'}
                                    >
                                        {/* Customer ID */}
                                        <td style={tdStyle('left')}>
                                            <div className="d-flex align-items-center gap-2" style={{ overflow: 'hidden' }}>
                                                <div style={{
                                                    width: 30, height: 30, borderRadius: '50%',
                                                    background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#fff', fontWeight: 700, fontSize: '0.65rem', flexShrink: 0,
                                                    letterSpacing: '-0.5px',
                                                }}>
                                                    {(pref.customerName || String(pref.customerId))
                                                        .split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                                                </div>
                                                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                    <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={pref.customerName}>
                                                        {pref.customerName || `Customer #${pref.customerId}`}
                                                    </div>
                                                    <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                                                        ID: {pref.customerId}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Toggle per field */}
                                        {PREFERENCE_FIELDS.map(f => (
                                            <td key={f.key} style={tdStyle('center')}>
                                                <Tooltip title={`${pref.customerName || `Customer #${pref.customerId}`} - ${f.label}`} arrow placement="top">
                                                    <div style={{ display: 'inline-block' }}>
                                                        <Toggle
                                                            checked={pref[f.key] === true}
                                                            onChange={val => handleToggle(pref.preferenceId, f.key, val)}
                                                            disabled={saving === pref.preferenceId}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </td>
                                        ))}

                                        {/* Save button */}
                                        <td style={tdStyle('center')}>
                                            <button
                                                onClick={() => handleSave(pref)}
                                                disabled={saving === pref.preferenceId}
                                                style={{
                                                    padding: '4px 10px',
                                                    borderRadius: 14,
                                                    border: 'none',
                                                    background: saving === pref.preferenceId
                                                        ? '#94a3b8'
                                                        : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                    fontSize: '0.7rem',
                                                    cursor: saving === pref.preferenceId ? 'wait' : 'pointer',
                                                    transition: 'all 0.2s',
                                                    boxShadow: '0 2px 4px rgba(37,99,235,0.25)',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {saving === pref.preferenceId ? '⏳' : '💾 Save'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ── Footer summary ── */}
                {!loading && filtered.length > 0 && (
                    <div className="px-3 py-2 border-top d-flex align-items-center justify-content-between"
                        style={{ background: '#f8fafc', fontSize: '0.75rem', color: '#64748b' }}>
                        <span>Showing <strong>{filtered.length}</strong> of <strong>{preferences.length}</strong> customers</span>
                        <button
                            onClick={fetchPreferences}
                            style={{
                                background: 'none', border: '1px solid #cbd5e1', borderRadius: 6,
                                padding: '2px 8px', fontSize: '0.72rem', cursor: 'pointer', color: '#475569',
                            }}
                        >
                            🔄 Refresh
                        </button>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

// ─── Table Style Helpers ──────────────────────────────────────────────────────
const thStyle = (minWidth, align = 'center') => ({
    padding: '6px 2px',
    textAlign: align,
    fontWeight: 700,
    fontSize: '0.66rem',
    color: '#475569',
    width: minWidth || 'auto',
    whiteSpace: 'normal',
});

const tdStyle = (align = 'left') => ({
    padding: '5px 2px',
    textAlign: align,
    verticalAlign: 'middle',
});

export default OrderPreferenceSettings;

